import styles from './styles.module.scss';

import { useContext, useEffect, useState } from 'react';
import TrilogicIconLogin from '../../../../assets/images/icons/trilogic-icon-login.png';
import { useNavigate } from 'react-router-dom';
import { LoginBox } from '../../../../components/LoginBox';
import { MessageContext } from '../../../../providers/MessageProvider';
import { resetPassword, sendResetPasswordEmail } from './requestHandlers';
import { verifyRoutePermissions } from '../../../../globalFunctions';
import { LoadingContext } from '../../../../providers/LoadingProvider';

export const ResetPasswordRoute = () => {
    const { setOpenMessage, setMessage } = useContext(MessageContext);
    const { setIsLoadingContent } = useContext(LoadingContext);
    const navigate = useNavigate();

    // Get the URL search params
    const searchParams = new URLSearchParams(window.location.hash.split('?')[1]);
    const resetPasswordEmail = searchParams.get('email');
    const resetPasswordToken = searchParams.get('token');

    const [openRegisterBox, setOpenRegisterBox] = useState<boolean>(true);
    const [email, setEmail] = useState<string>(resetPasswordEmail ?? '');
    const [password, setPassword] = useState<string>('');
    const [passwordType, setPasswordType] = useState<'password' | 'text'>('password');
    const [passwordConfirmation, setPasswordConfirmation] = useState<string>('');
    const [passwordConfirmationType, setPasswordConfirmationType] = useState<'password' | 'text'>(
        'password'
    );

    const handleBackPress = () => {
        navigate('/auth/sign-in?open=true');
    };

    const validateCredentials = () => {
        let message = '';

        if (email.trim() === '') {
            message = 'Preencha o campo de email';
        } else if (password.trim() === '') {
            message = 'Preecha o campo de senha';
        } else if (password.trim() !== passwordConfirmation.trim()) {
            message = 'As senhas informadas não são iguais';
        }

        if (message === '') {
            return true;
        } else {
            setMessage({
                type: 'warning',
                content: message
            });
            setOpenMessage(true);

            return false;
        }
    };

    const handleSendResetPassword = async (event: React.ChangeEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (validateCredentials()) {
            setIsLoadingContent(true);

            const result = await resetPassword({
                token: resetPasswordToken ?? '',
                email: email,
                password: password,
                password_confirmation: passwordConfirmation
            });

            if (result && typeof result === 'object' && !Object.keys(result).includes('error')) {
                setMessage({
                    type: 'success',
                    content: 'Senha redefinida com sucesso'
                });
                setOpenMessage(true);

                setTimeout(() => {
                    setIsLoadingContent(false);
                    navigate('/auth/sign-in?open=true');
                }, 1000);
            } else {
                const error = result as Error;

                setMessage({
                    type: 'error',
                    content: error.message
                });
                setOpenMessage(true);

                setIsLoadingContent(false);
            }
        }
    };

    const validateEmail = () => {
        if (email.trim() === '') {
            setMessage({
                type: 'warning',
                content: 'Preencha o campo de email'
            });
            setOpenMessage(true);

            return false;
        } else {
            return true;
        }
    };

    const handleSendResetPasswordLink = async (event: React.ChangeEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (validateEmail()) {
            setIsLoadingContent(true);

            const result = await sendResetPasswordEmail({
                email: email
            });

            if (result && typeof result === 'object' && !Object.keys(result).includes('error')) {
                setMessage({
                    type: 'success',
                    content: `O link para redefinição de senha foi encaminhado para o e-mail "${email}"`
                });
                setOpenMessage(true);

                setTimeout(() => {
                    setIsLoadingContent(false);
                    navigate('/auth/sign-in?open=true');
                }, 1000);
            } else {
                const error = result as Error;

                setMessage({
                    type: 'error',
                    content: error.message
                });
                setOpenMessage(true);

                setIsLoadingContent(false);
            }
        }
    };

    const togglePassword = () => {
        if (passwordType === 'password') {
            setPasswordType('text');
        } else {
            setPasswordType('password');
        }
    };

    const togglePasswordConfirmation = () => {
        if (passwordConfirmationType === 'password') {
            setPasswordConfirmationType('text');
        } else {
            setPasswordConfirmationType('password');
        }
    };

    useEffect(() => {
        verifyRoutePermissions(['not.auth'], navigate);
    }, []);

    return (
        <div className={styles.wrapper}>
            <div className={styles.mainLeft}></div>

            <div className={styles.mainRight}>
                <img src={TrilogicIconLogin} alt='Logo do Trilogic' />
            </div>

            <LoginBox open={openRegisterBox}>
                <div className={styles.nav}>
                    <div
                        className={`fa fa-chevron-left ${styles.green}`}
                        aria-hidden='true'
                        onClick={handleBackPress}
                    ></div>
                </div>

                {resetPasswordToken ? (
                    <div className={`${styles.loginOption} ${styles.credentials}`}>
                        <div className={`${styles.optionHeader} ${styles.centralize}`}>
                            Redefinição de senha
                        </div>

                        <form onSubmit={handleSendResetPassword}>
                            <div className={styles.groupField}>
                                <label className={styles.required} htmlFor='email'>
                                    Confirme seu e-mail
                                </label>
                                <input
                                    type='email'
                                    id='email'
                                    name='email'
                                    value={email}
                                    disabled={resetPasswordEmail !== null}
                                    onChange={event => setEmail(event.target.value)}
                                />
                            </div>

                            <div className={styles.groupField}>
                                <label className={styles.required} htmlFor='password'>
                                    Nova senha
                                </label>
                                <div className={styles.passwordField}>
                                    <input
                                        minLength={5}
                                        type={passwordType}
                                        id='password'
                                        name='password'
                                        value={password}
                                        onChange={event => setPassword(event.target.value)}
                                    />
                                    <div
                                        className={`fa ${
                                            passwordType === 'password' ? 'fa-eye-slash' : 'fa-eye'
                                        }`}
                                        onClick={togglePassword}
                                    ></div>
                                </div>
                            </div>

                            <div className={styles.groupField}>
                                <label className={styles.required} htmlFor='passwordConfirmation'>
                                    Confirme sua nova senha
                                </label>
                                <div className={styles.passwordField}>
                                    <input
                                        minLength={5}
                                        type={passwordConfirmationType}
                                        id='passwordConfirmation'
                                        name='passwordConfirmation'
                                        value={passwordConfirmation}
                                        onChange={event =>
                                            setPasswordConfirmation(event.target.value)
                                        }
                                    />
                                    <div
                                        className={`fa ${
                                            passwordConfirmationType === 'password'
                                                ? 'fa-eye-slash'
                                                : 'fa-eye'
                                        }`}
                                        onClick={togglePasswordConfirmation}
                                    ></div>
                                </div>
                            </div>

                            <button
                                type='submit'
                                className={`${styles.loginOption} ${styles.submitButton}`}
                            >
                                Enviar
                            </button>
                        </form>
                    </div>
                ) : (
                    <div className={`${styles.loginOption} ${styles.credentials}`}>
                        <div className={`${styles.optionHeader} ${styles.centralize}`}>
                            Recuperação de senha
                        </div>

                        <form onSubmit={handleSendResetPasswordLink}>
                            <div className={styles.groupField}>
                                <label className={styles.required} htmlFor='email'>
                                    E-mail
                                </label>
                                <input
                                    type='email'
                                    id='email'
                                    name='email'
                                    value={email}
                                    onChange={event => setEmail(event.target.value)}
                                />
                            </div>

                            <button
                                type='submit'
                                className={`${styles.loginOption} ${styles.submitButton}`}
                            >
                                Enviar
                            </button>
                        </form>
                    </div>
                )}
            </LoginBox>
        </div>
    );
};
